import React, { useState } from 'react';
import { TestEngine } from './components/TestEngine/TestEngine';
import { EnhancedResultsView } from './components/ResultsView/ResultsView';
import { mathQuestions, chineseQuestions } from './data/questions';
import { Subject, DiagnosticResult, Question, UserResponse } from './types';
import { Brain, Calculator, BookText, Sparkles, Target, MessageCircle, Lightbulb } from 'lucide-react';
import './App.css';

type AppState = 'welcome' | 'subject-selection' | 'testing' | 'results';

function App() {
  const [currentState, setCurrentState] = useState<AppState>('welcome');
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [testResult, setTestResult] = useState<DiagnosticResult | null>(null);
  const [testQuestions, setTestQuestions] = useState<Question[]>([]);
  const [testResponses, setTestResponses] = useState<UserResponse[]>([]);

  const handleSubjectSelect = (subject: Subject) => {
    setSelectedSubject(subject);
    setCurrentState('testing');
  };

  const handleTestComplete = (result: DiagnosticResult, questions: Question[], responses: UserResponse[]) => {
    setTestResult(result);
    setTestQuestions(questions);
    setTestResponses(responses);
    setCurrentState('results');
  };

  const handleRestart = () => {
    setSelectedSubject(null);
    setTestResult(null);
    setTestQuestions([]);
    setTestResponses([]);
    setCurrentState('welcome');
  };

  const getQuestions = (subject: Subject) => {
    return subject === 'math' ? mathQuestions : chineseQuestions;
  };

  // 欢迎页面
  if (currentState === 'welcome') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6 shadow-lg">
              <Brain className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              AI学习能力诊断系统
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              通过科学的测试题目，深度分析您的学习能力特征，配备AI学习伴侣，为您提供个性化的学习建议
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500 mb-12">
              <div className="flex items-center space-x-2">
                <Sparkles className="h-4 w-4" />
                <span>AI深度分析</span>
              </div>
              <div className="flex items-center space-x-2">
                <Brain className="h-4 w-4" />
                <span>6维度评估</span>
              </div>
              <div className="flex items-center space-x-2">
                <BookText className="h-4 w-4" />
                <span>智能提示系统</span>
              </div>
              <div className="flex items-center space-x-2">
                <Target className="h-4 w-4" />
                <span>AI学习伴侣</span>
              </div>
            </div>
          </div>

          {/* 功能特色 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Brain className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">全面诊断</h3>
              <p className="text-gray-600">
                从信息处理速度、工作记忆、抽象思维等6个维度全面评估学习能力，生成详细的能力画像
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI智能助手</h3>
              <p className="text-gray-600">
                配备AI学习伴侣，答题过程中提供实时对话支持，智能提示系统根据表现自动给出个性化指导
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">个性化分析</h3>
              <p className="text-gray-600">
                基于大模型深度分析学习模式，识别优势与不足，提供科学的学习建议和改进方案
              </p>
            </div>
          </div>

          {/* 新增：核心功能展示 */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 mb-16">
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">核心功能体验</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* AI学习伴侣 */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <MessageCircle className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">AI学习伴侣</h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• 实时对话交流，随时解答学习困惑</li>
                  <li>• 根据答题表现主动给出鼓励和建议</li>
                  <li>• 个性化学习指导，陪伴整个测试过程</li>
                  <li>• 情感支持，增强学习信心</li>
                </ul>
              </div>

              {/* 智能提示系统 */}
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                    <Lightbulb className="h-6 w-6 text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">智能提示系统</h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• 分析答题时间，智能判断是否需要帮助</li>
                  <li>• 三级渐进式提示，循序渐进引导思考</li>
                  <li>• 不直接给答案，培养独立思考能力</li>
                  <li>• 个性化提示内容，针对不同题型优化</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 测试流程 */}
          <div className="text-center mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">测试流程</h2>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg mb-2">1</div>
                <span className="text-sm text-gray-600">选择学科</span>
              </div>
              <div className="hidden md:block text-gray-400">→</div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg mb-2">2</div>
                <span className="text-sm text-gray-600">AI伴侣陪伴答题</span>
              </div>
              <div className="hidden md:block text-gray-400">→</div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg mb-2">3</div>
                <span className="text-sm text-gray-600">智能提示辅助</span>
              </div>
              <div className="hidden md:block text-gray-400">→</div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg mb-2">4</div>
                <span className="text-sm text-gray-600">AI深度分析报告</span>
              </div>
            </div>
          </div>

          {/* 开始按钮 */}
          <div className="text-center">
            <button
              onClick={() => setCurrentState('subject-selection')}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-lg font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              <Brain className="h-6 w-6 mr-2" />
              开始AI能力诊断
            </button>
            <p className="text-sm text-gray-500 mt-4">
              大约需要5-8分钟 | 配备AI学习伴侣全程陪伴
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 学科选择页面（直接跳过模式选择）
  if (currentState === 'subject-selection') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">选择测试学科</h2>
            <p className="text-lg text-gray-600">
              请选择您想要进行能力诊断的学科领域
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 数学 */}
            <div 
              onClick={() => handleSubjectSelect('math')}
              className="group cursor-pointer bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-transparent hover:border-blue-200"
            >
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-200 transition-colors">
                  <Calculator className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">数学能力诊断</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  测试您的数学计算、逻辑推理、问题解决等核心数学思维能力
                </p>
                <div className="space-y-2 text-sm text-gray-500 mb-6">
                  <div>• 基础计算能力</div>
                  <div>• 逻辑推理思维</div>
                  <div>• 数学应用能力</div>
                  <div>• 模式识别能力</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <span className="text-blue-700 font-medium">约5-8分钟 | 5道题目</span>
                </div>
              </div>
            </div>

            {/* 语文 */}
            <div 
              onClick={() => handleSubjectSelect('chinese')}
              className="group cursor-pointer bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-transparent hover:border-purple-200"
            >
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-purple-200 transition-colors">
                  <BookText className="h-10 w-10 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">语文能力诊断</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  评估您的语言理解、文字表达、文化素养等语文核心素养
                </p>
                <div className="space-y-2 text-sm text-gray-500 mb-6">
                  <div>• 词汇理解能力</div>
                  <div>• 语言逻辑思维</div>
                  <div>• 文化知识储备</div>
                  <div>• 表达组织能力</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <span className="text-purple-700 font-medium">约5-8分钟 | 5道题目</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => setCurrentState('welcome')}
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              ← 返回首页
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 测试进行中
  if (currentState === 'testing' && selectedSubject) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto py-8">
          <TestEngine
            questions={getQuestions(selectedSubject)}
            subject={selectedSubject}
            onTestComplete={handleTestComplete}
          />
        </div>
      </div>
    );
  }

  // 结果展示
  if (currentState === 'results' && testResult && selectedSubject && testQuestions.length > 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <EnhancedResultsView
          result={testResult}
          subject={selectedSubject}
          questions={testQuestions}
          responses={testResponses}
          onRestart={handleRestart}
          testMode="classic"
        />
      </div>
    );
  }

  return null;
}

export default App;